import React, { useState, useRef, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { createProcedureRevisionStatus } from "../../../../redux/procedureRevisionStatusSlice";
import { getPendingTasksCount, getRevAllNodes } from "../../../../redux/procedureRevisionSlice";
import { BASE_URL } from "../../../../config";
import { extractVersionStructure, getFieldsIncluded } from "../../../../util/getAuditTrailData";
import RenderHtml from "../../../../common/AppSpecificConpnents/PartsTable/RenderHtml";
import Snackbar from "../../../../common/Snackbar";
import SignatureField from "../../../../common/FormFields/SignatureField";
import { ModalActions } from "../../../../common/Buttons";
import RenderCheckbox from "./RenderCheckbox";
import RenderArchiveLabel from "./RenderArchiveLabel";
import { revisionSort } from "../../Utils/utils.js";
import { sameRevisionLetterFamily } from "../../../../util/trainingLogStatus";

/**
 * ReleaseRevisionModal - Modal for releasing a revision
 */
const ReleaseRevisionModal = ({
  isOpen,
  onClose,
  partId,
  partNumber,
  type,
  description,
  myRevision,
  revisionId,
  revisionData,
  onSuccess,
}) => {
  const [revisionDetails, setRevisionDetails] = useState("");
  const [password, setPassword] = useState("");
  const [signature, setSignature] = useState("");
  const [isReviewed, setIsReviewed] = useState(false);
  const [isReleasing, setIsReleasing] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "",
  });

  const sigPadRef = useRef();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pendingTasksCountData } = useSelector((state) => state.procedureRevision);

  useEffect(() => {
    if (isOpen && partId && revisionId) {
      dispatch(getPendingTasksCount({ partId, revisionId }));
    }
  }, [isOpen, partId, revisionId, dispatch]);

  // Get signature auth methods from localStorage
  const [authMethods, setAuthMethods] = useState([]);
  useEffect(() => {
    try {
      const storedMethods = localStorage.getItem("signature_auth_methods");
      if (storedMethods) {
        const methods = JSON.parse(storedMethods);
        setAuthMethods(Array.isArray(methods) ? methods : []);
      } else {
        const userInfo = localStorage.getItem("loggedinUserInfo");
        if (userInfo) {
          try {
            const parsed = JSON.parse(userInfo);
            if (
              parsed.signature_auth_methods &&
              Array.isArray(parsed.signature_auth_methods)
            ) {
              setAuthMethods(parsed.signature_auth_methods);
            } else {
              setAuthMethods(["password", "signature"]);
            }
          } catch (e) {
            setAuthMethods(["password", "signature"]);
          }
        } else {
          setAuthMethods(["password", "signature"]);
        }
      }
    } catch (error) {
      console.error("Error parsing signature_auth_methods:", error);
      setAuthMethods(["password", "signature"]);
    }
  }, []);

  const handleEndDrawing = () => {
    if (sigPadRef.current?.getCanvas()) {
      const dataUrl = sigPadRef.current.getCanvas().toDataURL("image/png");
      setSignature(dataUrl);
    }
  };

  const handleClearSignature = () => {
    setSignature("");
  };

  const showSnackbar = (message, type) => {
    setSnackbar({ open: true, message, type });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: "", type: "" });
  };

  // Parse revision function (same as ReleaseRevision.js)
  const parseRevision = (rev) => {
    if (!rev) return null;

    const hasSpecialChars = /[@#$%!^&*]/.test(rev);
    if (hasSpecialChars) {
      return {
        base: "",
        type: "special_chars",
        minorNum: 0,
        amendChar: "",
        original: rev,
      };
    }

    if (rev === "-") {
      return {
        base: "",
        type: "special",
        minorNum: 0,
        amendChar: "",
        original: rev,
      };
    }

    if (/^[A-Z]{1,3}-$/.test(rev)) {
      return {
        base: rev.slice(0, -1),
        type: "major",
        minorNum: 0,
        amendChar: "",
        original: rev,
      };
    }

    const amendMatchWithHyphen = rev.match(/^([A-Z]{1,3})(\d+)-([a-z]+)$/);
    if (amendMatchWithHyphen) {
      const [, base, num, amendChar] = amendMatchWithHyphen;
      return {
        base,
        type: "amend",
        minorNum: parseInt(num),
        amendChar,
        original: rev,
      };
    }

    const amendMinorlessMatch = rev.match(/^([A-Z]{1,3})-([a-z]+)$/);
    if (amendMinorlessMatch) {
      const [, base, amendChar] = amendMinorlessMatch;
      return {
        base,
        type: "amend_minorless",
        minorNum: 0,
        amendChar,
        original: rev,
      };
    }

    const minorMatch = rev.match(/^([A-Z]{1,3})(\d+)([a-z]*)$/);
    if (minorMatch) {
      const [, base, num, amendChar] = minorMatch;
      return {
        base,
        type: amendChar ? "amend" : "minor",
        minorNum: parseInt(num),
        amendChar: amendChar || "",
        original: rev,
      };
    }

    return null;
  };

  // Helper function to check if one revision is above another
  const isRevisionAbove = (currentRev, compareRev) => {
    const currentParsed = parseRevision(currentRev);
    const compareParsed = parseRevision(compareRev);

    if (!currentParsed || !compareParsed) return false;
    if (currentParsed.base !== compareParsed.base) return false;

    if (compareParsed.minorNum < currentParsed.minorNum) return true;

    if (compareParsed.minorNum === currentParsed.minorNum) {
      if (currentParsed.amendChar && !compareParsed.amendChar) return true;
      if (compareParsed.amendChar && currentParsed.amendChar) {
        return compareParsed.amendChar < currentParsed.amendChar;
      }
      if (compareParsed.amendChar && !currentParsed.amendChar) return true;
    }

    return false;
  };

  const { revisionsToReject, revisionToArchive } = useMemo(() => {
    if (!pendingTasksCountData || !Array.isArray(pendingTasksCountData))
      return { revisionsToReject: [], revisionToArchive: null };

    const toReject = pendingTasksCountData.filter(
      (item) =>
        item.status_changed_to !== "Released" &&
        item.procedure_revision_id !== revisionId
    );

    /** Superseded release: the Released revision immediately before this one in sort order (not first Released in API order). */
    let toArchive = null;
    if (
      Array.isArray(revisionData) &&
      revisionData.length > 0 &&
      revisionId
    ) {
      const sorted = [...revisionData].sort(revisionSort);
      const idx = sorted.findIndex(
        (r) => r.procedure_revision_id === revisionId
      );
      if (idx > 0) {
        const preceding = sorted.slice(0, idx);
        for (let i = preceding.length - 1; i >= 0; i--) {
          const rev = preceding[i];
          const latest = rev.status_changes?.find((sc) => sc.is_latest);
          if (latest?.status_changed_to === "Released") {
            const match = pendingTasksCountData.find(
              (p) =>
                p.procedure_revision_id === rev.procedure_revision_id &&
                p.status_changed_to === "Released"
            );
            toArchive =
              match ??
              ({
                rev: rev.rev,
                task_count: 0,
                procedure_revision_id: rev.procedure_revision_id,
                status_changed_to: "Released",
              });
            break;
          }
        }
      }
    }

    if (!toArchive) {
      toArchive =
        pendingTasksCountData.find(
          (item) =>
            item.status_changed_to === "Released" &&
            item.procedure_revision_id !== revisionId
        ) ?? null;
    }

    return {
      revisionsToReject: toReject,
      revisionToArchive: toArchive,
    };
  }, [pendingTasksCountData, revisionData, revisionId]);

  /**
   * Nearest Released revision above the current row in table sort order (revisionSort).
   * Used only for copy-training-logs — no pendingTasksCount fallback (must be strictly "above").
   */
  const previousReleasedRevisionIdAbove = useMemo(() => {
    if (!Array.isArray(revisionData) || !revisionData.length || !revisionId) {
      return null;
    }
    const sorted = [...revisionData].sort(revisionSort);
    const idx = sorted.findIndex(
      (r) => r.procedure_revision_id === revisionId
    );
    if (idx <= 0) return null;
    const preceding = sorted.slice(0, idx);
    for (let i = preceding.length - 1; i >= 0; i--) {
      const rev = preceding[i];
      const latest = rev.status_changes?.find((sc) => sc.is_latest);
      if (latest?.status_changed_to === "Released") {
        return rev.procedure_revision_id;
      }
    }
    return null;
  }, [revisionData, revisionId]);

  /**
   * Row immediately above the current revision in table sort order (not necessarily Released).
   * copy-training-logs runs only when this row shares the same letter family as the revision being released.
   */
  const immediateRevisionAboveInSort = useMemo(() => {
    if (!Array.isArray(revisionData) || !revisionData.length || !revisionId) {
      return null;
    }
    const sorted = [...revisionData].sort(revisionSort);
    const idx = sorted.findIndex(
      (r) => r.procedure_revision_id === revisionId
    );
    if (idx <= 0) return null;
    return sorted[idx - 1];
  }, [revisionData, revisionId]);

  const handleReleaseRevision = async () => {
    if (!revisionDetails) {
      showSnackbar("Comment is mandatory.", "error");
      return;
    }
    // Only validate password if it's in auth methods
    if (authMethods.includes("password") && !password) {
      showSnackbar("Password is mandatory.", "error");
      return;
    }
    // Only validate signature if it's in auth methods
    if (authMethods.includes("signature")) {
      const isSignatureEmpty = sigPadRef.current?.isEmpty() ?? true;
      if (!signature || isSignatureEmpty) {
        showSnackbar("Signature is mandatory.", "error");
        return;
      }
    }
    if (revisionsToReject.length > 0 && !isReviewed) {
      showSnackbar(
        "Please select the checkbox of review the revisions before Releasing.",
        "error"
      );
      return;
    }

    setIsReleasing(true);

    try {
      let user_ref = localStorage.getItem("user_id");

      const prevData = revisionData.find(
        (r) => r.procedure_revision_id === revisionId
      );

      // Fetch all revision nodes data
      let revisionNodes = [];
      try {
        const nodesResult = await dispatch(
          getRevAllNodes({ partId, revisionId })
        ).unwrap();
        
        if (nodesResult) {
          // Extract version content using extractVersionStructure
          // revisionNodes = extractVersionStructure(nodesResult) || [];
          revisionNodes = nodesResult || [];
        }
      } catch (error) {
        console.error("Error fetching revision nodes:", error);
        // Continue with empty array if fetch fails
      }

      // Check if this is an amended revision
      const currentRevisionParsed = parseRevision(myRevision);
      const isAmendedRevision =
        currentRevisionParsed?.type === "amend" ||
        currentRevisionParsed?.type === "amend_minorless";

      // Handle special cases for amended revisions
      if (isAmendedRevision) {
        // Find all revisions in the same minor revision series
        const minorSeriesKey = `${currentRevisionParsed.base}${currentRevisionParsed.minorNum}`;
        const revisionsInSameSeries = revisionData.filter((rev) => {
          const parsed = parseRevision(rev.rev);
          return (
            parsed && `${parsed.base}${parsed.minorNum}` === minorSeriesKey
          );
        });

        // Last Released in this series *before* the current revision (sorted order), not first Released in API order
        const sortedSeries = [...revisionsInSameSeries].sort(revisionSort);
        const idxInSeries = sortedSeries.findIndex(
          (r) => r.procedure_revision_id === revisionId
        );
        let previouslyReleasedRevision = null;
        if (idxInSeries > 0) {
          const precedingInSeries = sortedSeries.slice(0, idxInSeries);
          for (let i = precedingInSeries.length - 1; i >= 0; i--) {
            const rev = precedingInSeries[i];
            const latestStatus = rev.status_changes?.find((sc) => sc.is_latest);
            if (latestStatus?.status_changed_to === "Released") {
              previouslyReleasedRevision = rev;
              break;
            }
          }
        }

        // Find other amended revisions in this series that are ABOVE the current amendment
        const otherAmendedRevisions = revisionsInSameSeries.filter((rev) => {
          if (rev.procedure_revision_id === revisionId) return false;
          const parsed = parseRevision(rev.rev);
          const latestStatus = rev.status_changes?.find((sc) => sc.is_latest);

          const isActiveAmendment =
            (parsed?.type === "amend" || parsed?.type === "amend_minorless") &&
            [
              "Not Submitted",
              "Submitted For Approval",
              "Approved",
              "Change Requested",
            ].includes(latestStatus?.status_changed_to);

          if (!isActiveAmendment) return false;
          return isRevisionAbove(myRevision, rev.rev);
        });

        // Reject other amended revisions in the series
        for (const rev of otherAmendedRevisions) {
          try {
            const rejectFormData = {
              procedure_revision_id_ref: rev.procedure_revision_id,
              status_changed_to: "Rejected",
              is_in_release_history: true,
              user_ref,
              is_open: true,
              purpose: "Reject",
              is_latest: true,
              past_procedure_revisions_reviewed: "{}",
              audit_trail_data: {
                signature: authMethods.includes("signature") ? signature : "",
                ...(authMethods.includes("password") && password
                  ? { hash: password }
                  : {}),
                ...(!authMethods.includes("password")
                  ? {
                      is_password_required: false,
                    }
                  : {
                      is_password_required: true,
                    }),
                action: "REVISION_REJECTED",
                comment: `Rejected due to release of amended revision ${myRevision}`,
                parent_version: rev.version_id_ref,
                version_content: {
                  ...rev,
                  action_to_show: `Rejected ${partNumber} Rev ${rev.rev} due to release of ${myRevision}`,
                  procedure_number: partNumber,
                  type: type,
                  description: description,
                  item_code_type:type,
                  partNumber: partNumber,
                  status: "Rejected",
                  revisionNodes: revisionNodes,
                  ...getFieldsIncluded()
                },
                password,
              },
            };
            await dispatch(
              createProcedureRevisionStatus(rejectFormData)
            ).unwrap();
          } catch (error) {
            console.error(`Error rejecting revision ${rev.rev}:`, error);
          }
        }

        // Case 1: Amendment to Archived Revision
        if (previouslyReleasedRevision) {
          const latestStatus = previouslyReleasedRevision.status_changes?.find(
            (sc) => sc.is_latest
          );
          if (latestStatus?.status_changed_to === "Archived") {
            // Set the amended revision to archived status
            const formData = {
              procedure_revision_id_ref: revisionId,
              status_changed_to: "Archived",
              is_in_release_history: true,
              user_ref,
              is_open: false,
              purpose: "Release",
              is_latest: true,
              past_procedure_revisions_reviewed: "{}",
              part_id_ref: partId,
              audit_trail_data: {
                signature: authMethods.includes("signature") ? signature : "",
                ...(authMethods.includes("password") && password
                  ? { hash: password }
                  : {}),
                ...(!authMethods.includes("password")
                  ? {
                      is_password_required: false,
                    }
                  : {
                      is_password_required: true,
                    }),
                action: "REVISION_ARCHIVED",
                comment: revisionDetails,
                parent_version: prevData.version_id_ref,
                version_content: {
                  ...prevData,
                  action_to_show: `Archived ${partNumber} Rev ${myRevision} (Amendment to Archived Revision)`,
                  procedure_number: partNumber,
                  type: type,
                  description: description,
                  item_code_type:type,
                  partNumber: partNumber,
                  status: "Archived",
                  revisionNodes: revisionNodes,
                },
                password,
              },
            };

            try {
              await dispatch(createProcedureRevisionStatus(formData)).unwrap();
              showSnackbar(
                "Amended revision archived successfully!",
                "success"
              );
              navigate(
                `/archived-revision/${partId}/${myRevision}/${type}/${partNumber}/${description}/Archived/${revisionId}`
              );
              setIsReleasing(false);
              if (onSuccess) onSuccess();
              return;
            } catch (error) {
              showSnackbar(
                "Error archiving amended revision: " + error,
                "error"
              );
              setIsReleasing(false);
              return;
            }
          }
        }

        // Case 2: Amendment to Released Revision - Archive the previously released revision
        if (previouslyReleasedRevision) {
          const latestStatus = previouslyReleasedRevision.status_changes?.find(
            (sc) => sc.is_latest
          );
          if (latestStatus?.status_changed_to === "Released") {
            try {
              const archiveFormData = {
                procedure_revision_id_ref:
                  previouslyReleasedRevision.procedure_revision_id,
                status_changed_to: "Archived",
                is_in_release_history: true,
                user_ref,
                is_open: true,
                purpose: "Archive",
                is_latest: true,
                past_procedure_revisions_reviewed: "{}",
                audit_trail_data: {
                  signature: authMethods.includes("signature") ? signature : "",
                  ...(authMethods.includes("password") && password
                    ? { hash: password }
                    : {}),
                  ...(!authMethods.includes("password")
                    ? {
                        is_password_required: false,
                      }
                    : {
                        is_password_required: true,
                      }),
                  action: "REVISION_ARCHIVED",
                  comment: `Archived due to release of amended revision ${myRevision}`,
                  parent_version: previouslyReleasedRevision.version_id_ref,
                  version_content: {
                    ...previouslyReleasedRevision,
                    action_to_show: `Archived ${partNumber} Rev ${previouslyReleasedRevision.rev} due to release of ${myRevision}`,
                    procedure_number: partNumber,
                    type: type,
                    description: description,
                    item_code_type:type,
                    partNumber: partNumber,
                    status: "Archived",
                    revisionNodes: revisionNodes,
                  },
                  password,
                },
              };
              await dispatch(
                createProcedureRevisionStatus(archiveFormData)
              ).unwrap();
            } catch (error) {
              console.error(
                "Error archiving previously released revision:",
                error
              );
              setIsReleasing(false);
            }
          }
        }
      }

      // Handle major and minor revision releases - reject revisions above the current revision
      if (!isAmendedRevision) {
        const sortedRevisions = [...revisionData].sort((a, b) => {
          const revA = parseRevision(a.rev);
          const revB = parseRevision(b.rev);
          if (!revA && !revB) return 0;
          if (!revA) return 1;
          if (!revB) return -1;
          if (revA.type === "special") return -1;
          if (revB.type === "special") return 1;
          if (revA.base !== revB.base)
            return revA.base.localeCompare(revB.base);
          if (revA.type === "major" && revB.type !== "major") return -1;
          if (revB.type === "major" && revA.type !== "major") return 1;
          if (revA.minorNum !== revB.minorNum)
            return revA.minorNum - revB.minorNum;
          return revA.amendChar.localeCompare(revB.amendChar);
        });

        const currentRevisionIndex = sortedRevisions.findIndex(
          (rev) => rev.procedure_revision_id === revisionId
        );

        if (currentRevisionIndex !== -1) {
          // Get revisions that come before the current revision (above in hierarchy)
          const revisionsAbove = sortedRevisions.slice(0, currentRevisionIndex);

          // Find active revisions above the current revision that need to be rejected
          const activeRevisionsAbove = revisionsAbove.filter((rev) => {
            const latestStatus = rev.status_changes?.find((sc) => sc.is_latest);
            const parsed = parseRevision(rev.rev);
            // Only reject if NOT an amended revision
            const isAmended =
              parsed?.type === "amend" || parsed?.type === "amend_minorless";
            return (
              !isAmended &&
              [
                "Not Submitted",
                "Submitted For Approval",
                "Approved",
                "Change Requested",
              ].includes(latestStatus?.status_changed_to)
            );
          });

          // Reject all active non-amended revisions above the current revision
          for (const rev of activeRevisionsAbove) {
            try {
              const rejectFormData = {
                procedure_revision_id_ref: rev.procedure_revision_id,
                status_changed_to: "Rejected",
                is_in_release_history: true,
                user_ref,
                is_open: true,
                purpose: "Reject",
                is_latest: true,
                past_procedure_revisions_reviewed: "{}",
                audit_trail_data: {
                  signature: authMethods.includes("signature") ? signature : "",
                  ...(authMethods.includes("password") && password
                    ? { hash: password }
                    : {}),
                  ...(!authMethods.includes("password")
                    ? {
                        is_password_required: false,
                      }
                    : {
                        is_password_required: true,
                      }),
                  action: "REVISION_REJECTED",
                  comment: `Rejected due to release of revision ${myRevision}`,
                  parent_version: rev.version_id_ref,
                  version_content: {
                    ...rev,
                    action_to_show: `Rejected ${partNumber} Rev ${rev.rev} due to release of ${myRevision}`,
                    procedure_number: partNumber,
                    type: type,
                    description: description,
                    item_code_type:type,
                    partNumber: partNumber,
                    status: "Rejected",
                    revisionNodes: revisionNodes,
                  },
                  password,
                },
              };
              await dispatch(
                createProcedureRevisionStatus(rejectFormData)
              ).unwrap();
            } catch (error) {
              console.error(`Error rejecting revision ${rev.rev}:`, error);
            }
          }
        }
      }

      // Send final payload with revisionDataList for amended revisions
      const parsedRev = parseRevision(myRevision);
      const myRevisionData = [...revisionData].sort((a, b) => {
        const revA = parseRevision(a.rev);
        const revB = parseRevision(b.rev);
        if (!revA && !revB) return 0;
        if (!revA) return 1;
        if (!revB) return -1;
        if (revA.type === "special") return -1;
        if (revB.type === "special") return 1;
        if (revA.base !== revB.base) return revA.base.localeCompare(revB.base);
        if (revA.type === "major" && revB.type !== "major") return -1;
        if (revB.type === "major" && revA.type !== "major") return 1;
        if (revA.minorNum !== revB.minorNum)
          return revA.minorNum - revB.minorNum;
        return revA.amendChar.localeCompare(revB.amendChar);
      });

      const isAmended =
        parsedRev?.type === "amend" || parsedRev?.type === "amend_minorless";
      const isReleasing = true;

      const formData = {
        procedure_revision_id_ref: revisionId,
        status_changed_to: "Released",
        is_in_release_history: true,
        user_ref,
        is_open: false,
        purpose: "Release",
        is_latest: true,
        past_procedure_revisions_reviewed: "{}",
        part_id_ref: partId,
        tree_node_id:prevData?.tree_node_details?.tree_node_id,
        audit_trail_data: {
          signature: authMethods.includes("signature") ? signature : "",
          ...(authMethods.includes("password") && password
            ? { hash: password }
            : {}),
          ...(!authMethods.includes("password")
            ? {
                is_password_required: false,
              }
            : {
                is_password_required: true,
              }),
          action: "REVISION_RELEASED",
          comment: revisionDetails,
          parent_version: prevData?.version_id_ref,
          version_content: {
            ...prevData,
            action_to_show: `Released ${partNumber} Rev ${myRevision}`,
            procedure_number: partNumber,
            type: type,
            description: description,
            item_code_type:type,
            partNumber: partNumber,
            status: "Released",
            status_changed_to: "Released",
            revisionNodes: revisionNodes,
            revisionToArchive: revisionToArchive
              ? {
                  rev: revisionToArchive.rev,
                  task_count: revisionToArchive.task_count,
                }
              : null,
            revisionsToReject: revisionsToReject.map((r) => ({
              rev: r.rev,
              task_count: r.task_count,
            })),
          },
          password,
        },
      };

      const finalPayload = {
        ...formData,
        ...(isAmended && isReleasing
          ? { revisionDataList: myRevisionData }
          : {}),
        rev: myRevision,
      };

      await dispatch(createProcedureRevisionStatus(finalPayload)).unwrap();

      let trainingLogsCopyFailed = false;
      const currentRevLabelForCopy =
        prevData?.rev != null && String(prevData.rev).trim() !== ""
          ? String(prevData.rev).trim()
          : String(myRevision || "").trim();
      const sameLetterAsRevisionAbove =
        immediateRevisionAboveInSort?.rev != null &&
        sameRevisionLetterFamily(
          immediateRevisionAboveInSort.rev,
          currentRevLabelForCopy
        );
      if (previousReleasedRevisionIdAbove && sameLetterAsRevisionAbove) {
        try {
          await axios.post(
            `${BASE_URL}procedure-revision-status/copy-training-logs`,
            {
              old_revision_ref: previousReleasedRevisionIdAbove,
              new_revision_ref: revisionId,
            }
          );
        } catch (copyErr) {
          console.error("copy-training-logs failed:", copyErr);
          trainingLogsCopyFailed = true;
        }
      }

      if (trainingLogsCopyFailed) {
        showSnackbar(
          "Revision released, but training logs could not be copied from the previous release.",
          "warning"
        );
      } else {
        showSnackbar("Revision released successfully!", "success");
      }

      // Navigate to released revision page
      navigate(
        `/released-revision/${partId}/${myRevision}/${type}/${partNumber}/no-description/${revisionId}`
      );

      setSnackbar({ open: false, message: "", type: "" });
      setIsReleasing(false);
      setRevisionDetails("");
      setPassword("");
      setSignature("");
      setIsReviewed(false);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      const errorMessage = error?.message || error || "Unknown error";
      // For "Incorrect password", show the message directly
      if (errorMessage === "Incorrect password") {
        showSnackbar("Incorrect password", "error");
      } else {
        showSnackbar("Error releasing revision: " + errorMessage, "error");
      }
      setIsReleasing(false);
    }
  };

  if (!isOpen) return null;

  // Get current revision
  const currentRev = revisionData?.find(
    (r) => r.procedure_revision_id === revisionId
  );

  return (
    <>
      <div
        className="modal-overlay"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1000,
        }}
      >
        <div
          className="modal-content1"
          style={{
            position: "relative",
            backgroundColor: "white",
            borderRadius: "8px",
            padding: "20px",
            width: "90%",
            maxWidth: "500px",
            maxHeight: "90vh",
            overflowY: "auto",
            overflowX: "hidden",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "8px",
            }}
          >
            <h3
              className="modal-header"
              style={{ fontSize: "14px", margin: 0 }}
            >
              Release Revision to Production
            </h3>
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
              title="Close"
            >
              <FontAwesomeIcon icon={faTimes} size="1x" color="gray" />
            </button>
          </div>
          <hr className="divider" />

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "baseline",
              columnGap: "12px",
              rowGap: "4px",
              marginLeft: "10px",
              marginBottom: "4px",
            }}
          >
            <strong style={{ fontSize: "15px" }}>
              {type} {partNumber}
            </strong>
            <RenderHtml
              content={currentRev?.part_description}
              inline
            />
          </div>

          <div className="form-group">
            <label style={{ fontSize: "13px", fontWeight: "bold" }}>
              Revision: {myRevision}
              <RenderArchiveLabel revisionToArchive={revisionToArchive} />
            </label>
          </div>

          <div className="form-group">
            <label>
              <span style={{ fontSize: "13px", fontWeight: "bold" }}>
                Released By:
              </span>{" "}
              {localStorage.getItem("user_name")}
            </label>
          </div>

          <div className="form-group">
            <label style={{ fontSize: "13px", fontWeight: "bold" }}>
              Comment<span style={{ color: "#0D6EFD" }}>*</span>
            </label>
            <textarea
              className="input-textarea"
              placeholder="Enter Comment..."
              value={revisionDetails}
              onChange={(e) => setRevisionDetails(e.target.value)}
              style={{
                height: "70px",
              }}
            />
          </div>

          {revisionsToReject.length > 0 ? (
            <RenderCheckbox
              revisionsToReject={revisionsToReject}
              isChecked={isReviewed}
              onChange={() => setIsReviewed(!isReviewed)}
            />
          ) : (<span style={{padding:"10px"}}></span>)}

          {/* Password - Only show if password is in auth methods */}
          {authMethods.includes("password") && (
            <div className="form-group" style={{ marginTop: "-12px" }}>
              <label style={{ fontSize: "13px", fontWeight: "bold" }}>
                Enter Password<span style={{ color: "#0D6EFD" }}>*</span>
              </label>
              <input
                type="password"
                className="input-field"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          )}

          {/* Signature - Only show if signature is in auth methods */}
          {authMethods.includes("signature") && (
            <SignatureField
              ref={sigPadRef}
              label="Sign Here"
              required={true}
              width="480px"
              height="100px"
              onEnd={handleEndDrawing}
              onClear={handleClearSignature}
            />
          )}

          <ModalActions
            onClose={onClose}
            onSubmit={handleReleaseRevision}
            submitLabel="Release"
            loading={isReleasing}
            loadingText="Releasing..."
          />
        </div>
      </div>

      {/* Snackbar */}
      {snackbar.open && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={handleCloseSnackbar}
        />
      )}
    </>
  );
};

export default ReleaseRevisionModal;
